import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Seo from "@/components/base/Seo";
import StatusBadge from "@/components/base/StatusBadge";
import DataTable, { type DataColumn } from "@/components/base/DataTable";
import AdminPageHeader from "../components/AdminPageHeader";
import AdminToolbar from "../components/AdminToolbar";
import AdminFilterTabs from "../components/AdminFilterTabs";
import { useAuth } from "@/context/AuthContext";
import { getAdvertisements } from "@/services/adminApi";
import type { Advertisement } from "@/types";

const statusTabs = [
  { value: "all", label: "All" },
  { value: "active", label: "Active" },
  { value: "paused", label: "Paused" },
  { value: "scheduled", label: "Scheduled" },
  { value: "expired", label: "Expired" },
];

export default function AdminAdvertisements() {
  const { token } = useAuth();
  const [rows, setRows] = useState<Advertisement[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("all");
  const [notice, setNotice] = useState("");

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(false);
    getAdvertisements(token)
      .then((a) => active && setRows(a))
      .catch(() => active && setError(true))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [token]);

  const flash = (msg: string) => {
    setNotice(msg);
    setTimeout(() => setNotice(""), 2200);
  };

  const filtered = useMemo(() => {
    const q = query.toLowerCase().trim();
    return rows.filter((r) => {
      const matchesStatus = status === "all" || r.status === status;
      const matchesQuery =
        !q || r.title.toLowerCase().includes(q) || r.advertiser.toLowerCase().includes(q) || r.placement.toLowerCase().includes(q);
      return matchesStatus && matchesQuery;
    });
  }, [rows, query, status]);

  const togglePause = (id: string) => {
    setRows((prev) =>
      prev.map((r) => (r.id === id ? { ...r, status: r.status === "paused" ? "active" : "paused" } : r)),
    );
    flash("Campaign status updated.");
  };

  const totals = useMemo(() => {
    const impressions = rows.reduce((s, r) => s + r.impressions, 0);
    const clicks = rows.reduce((s, r) => s + r.clicks, 0);
    return { impressions, clicks, ctr: impressions ? (clicks / impressions) * 100 : 0 };
  }, [rows]);

  const columns: DataColumn<Advertisement>[] = [
    {
      key: "title",
      header: "Campaign",
      render: (r) => (
        <div className="min-w-0">
          <p className="truncate font-semibold text-foreground-950">{r.title}</p>
          <p className="truncate text-xs text-foreground-500">{r.advertiser}</p>
        </div>
      ),
    },
    { key: "placement", header: "Placement", render: (r) => <span className="text-foreground-700">{r.placement}</span>, nowrap: true },
    { key: "status", header: "Status", render: (r) => <StatusBadge status={r.status} />, nowrap: true },
    { key: "startAt", header: "Start", render: (r) => <span className="text-foreground-500">{r.startAt}</span>, nowrap: true },
    { key: "endAt", header: "End", render: (r) => <span className="text-foreground-500">{r.endAt}</span>, nowrap: true },
    { key: "impressions", header: "Impr.", align: "right", render: (r) => <span className="text-foreground-700">{r.impressions.toLocaleString()}</span>, nowrap: true },
    { key: "clicks", header: "Clicks", align: "right", render: (r) => <span className="text-foreground-700">{r.clicks.toLocaleString()}</span>, nowrap: true },
    {
      key: "ctr",
      header: "CTR",
      align: "right",
      nowrap: true,
      render: (r) => (
        <span className="font-semibold text-foreground-900">
          {r.impressions ? `${((r.clicks / r.impressions) * 100).toFixed(1)}%` : "—"}
        </span>
      ),
    },
    {
      key: "actions",
      header: "",
      align: "right",
      nowrap: true,
      render: (r) =>
        r.status === "active" || r.status === "paused" ? (
          <button
            type="button"
            onClick={() => togglePause(r.id)}
            className="inline-flex items-center gap-1 rounded-md border border-background-300 px-3 py-1.5 text-xs font-semibold text-foreground-700 hover:border-primary-400 whitespace-nowrap cursor-pointer"
          >
            <i className={r.status === "paused" ? "ri-play-line" : "ri-pause-line"}></i>
            {r.status === "paused" ? "Resume" : "Pause"}
          </button>
        ) : (
          <span className="text-xs text-foreground-400">—</span>
        ),
    },
  ];

  return (
    <>
      <Seo title="Advertisements" path="/admin/advertisements" />

      <AdminPageHeader
        icon="ri-megaphone-line"
        title="Advertisements"
        subtitle="Every banner and sponsored placement booked across the site."
        actions={
          <>
            {notice && (
              <span className="inline-flex items-center gap-2 rounded-md border border-accent-200 bg-accent-50 px-4 py-2 text-sm text-accent-900">
                <i className="ri-checkbox-circle-line text-accent-600"></i> {notice}
              </span>
            )}
            <Link
              to="/admin/popup-ads"
              className="inline-flex items-center gap-2 rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm font-semibold text-foreground-800 hover:border-primary-400 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-window-2-line"></i> Popup ads
            </Link>
            <button
              type="button"
              onClick={() => flash("Opening the new-campaign form…")}
              className="inline-flex items-center gap-2 rounded-md bg-primary-500 px-4 py-2.5 text-sm font-semibold text-foreground-950 hover:bg-primary-600 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-add-line"></i> New campaign
            </button>
          </>
        }
      />

      <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="rounded-lg border border-background-200 bg-background-50 p-4">
          <span className="text-xs font-semibold uppercase tracking-wide text-foreground-500">Impressions</span>
          <p className="mt-2 font-heading text-2xl font-extrabold text-foreground-950">{totals.impressions.toLocaleString()}</p>
        </div>
        <div className="rounded-lg border border-background-200 bg-background-50 p-4">
          <span className="text-xs font-semibold uppercase tracking-wide text-foreground-500">Clicks</span>
          <p className="mt-2 font-heading text-2xl font-extrabold text-accent-700">{totals.clicks.toLocaleString()}</p>
        </div>
        <div className="rounded-lg border border-background-200 bg-background-50 p-4">
          <span className="text-xs font-semibold uppercase tracking-wide text-foreground-500">Average CTR</span>
          <p className="mt-2 font-heading text-2xl font-extrabold text-foreground-950">{totals.ctr.toFixed(1)}%</p>
        </div>
      </div>

      <div className="mt-6">
        <AdminToolbar query={query} onQuery={setQuery} placeholder="Search campaigns…">
          <AdminFilterTabs options={statusTabs} value={status} onChange={setStatus} />
        </AdminToolbar>
      </div>

      <div className="mt-4">
        {loading ? (
          <div className="h-96 animate-pulse rounded-lg border border-background-200 bg-background-100/60"></div>
        ) : error ? (
          <div className="rounded-lg border border-secondary-200 bg-secondary-50 p-6 text-center text-sm text-secondary-900">
            Couldn't load campaigns. Please refresh.
          </div>
        ) : (
          <DataTable columns={columns} rows={filtered} rowKey={(r) => r.id} minWidth="1120px" />
        )}
      </div>
    </>
  );
}